interface Data {
    [key: string]: {
        path: string;
        options?: any;
    }
}

let inMemoryRedirects: Data | null = null;
let lastCacheTime = 0;
const CACHE_LIFETIME_MS = 60 * 60 * 1000; // 1 hour in-memory cache

export default defineNuxtRouteMiddleware(async (to, from) => {
    if (import.meta.client) return;

    const fs = await import('node:fs/promises');
    const path = await import('node:path');

    const now = Date.now();
    if (!inMemoryRedirects || now - lastCacheTime > CACHE_LIFETIME_MS) {
        const storagePath = path.resolve(process.cwd(), 'storage', 'redirects', 'data.json');
        try {
            const fileStats = await fs.stat(storagePath);
            const fileContent = await fs.readFile(storagePath, 'utf-8');
            inMemoryRedirects = JSON.parse(fileContent) as Data;

            // Check if file is older than 1 week
            if (now - fileStats.mtimeMs > 604800000) {
                inMemoryRedirects = await fetchDataAndUpdate(storagePath, fs, path);
            }
        } catch (error) {
            inMemoryRedirects = await fetchDataAndUpdate(storagePath, fs, path);
        }
        lastCacheTime = now;
    }

    // Redirect if conditions in data are met
    if (inMemoryRedirects && inMemoryRedirects[to.path]) {
        return navigateTo(inMemoryRedirects[to.path]?.path, inMemoryRedirects[to.path]?.options);
    }

    return;
});

// Custom function to fetch new data and update the storage
async function fetchDataAndUpdate(storagePath: string, fs: typeof import('node:fs/promises'), path: typeof import('node:path')): Promise<Data> {
    try {
        const newData: any = await fetchData('/redirects');
        if (newData?.data) {
            await fs.mkdir(path.dirname(storagePath), { recursive: true });
            await fs.writeFile(storagePath, JSON.stringify(newData.data), 'utf-8');
            return newData.data;
        }
    } catch (error) {
        // Backend API may be unreachable during offline build or prerender
    }
    return {};
}